import type { ComponentProps } from 'react';

import { Button, type ButtonProps } from '@vs-shop/ui/button';
import {
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@vs-shop/ui/drawer';
import { clsxm } from '@vs-shop/utils';

import { optionalClassName } from '../../internal/optionalClassName.ts';

/**
 * The mobile filters drawer: the bottom sheet a listing opens below the width
 * where its filter rail has a column of its own, holding the same rail parts
 * the desktop column composes, with the two ways out of it pinned to its foot.
 *
 *   <Drawer direction="bottom" open={open} onOpenChange={setOpen}>
 *     <FilterDrawerTrigger>
 *       <FilterDrawerLabel>Filtrovat</FilterDrawerLabel>
 *       <ListFilter />
 *     </FilterDrawerTrigger>
 *     <FilterDrawerPanel aria-describedby={undefined}>
 *       <FilterDrawerBody>
 *         <FilterDrawerBar>
 *           <FilterDrawerTitle>Filtrování</FilterDrawerTitle>
 *           <FilterDrawerClose aria-label="Zavřít"><XMarkIcon /></FilterDrawerClose>
 *         </FilterDrawerBar>
 *         <FilterDrawerRail>{facets}</FilterDrawerRail>
 *       </FilterDrawerBody>
 *       <FilterDrawerFooter>
 *         <FilterDrawerApply onClick={…}>
 *           <FilterDrawerLabel>Zobrazit produkty</FilterDrawerLabel>
 *           <FilterDrawerCount>{total}</FilterDrawerCount>
 *         </FilterDrawerApply>
 *         <FilterDrawerClearAll onClick={…}>Vymazat</FilterDrawerClearAll>
 *       </FilterDrawerFooter>
 *     </FilterDrawerPanel>
 *   </Drawer>
 *
 * The `Drawer` root, its overlay and its focus handling are
 * `@vs-shop/ui/drawer`'s and stay there: this section is what the sheet
 * holds, never a second implementation of the dialog underneath it. Each
 * part here is that primitive's own piece wearing the section's recipe —
 * the trigger, the panel, the scrolling body, the title, the dismiss and the
 * footer — so the dialog contract reaches the section unchanged. The root is
 * the tenant's to open, because what opens it (an atom, a `useState`) and what
 * closes it (applying, clearing) is the tenant's flow.
 *
 * **The drawer adds no second rail.** What stands in `FilterDrawerRail` is the
 * same `./filterRail` and `./filterRange` composition the desktop column
 * renders, handed over as children.
 *
 * No `variant` anywhere: the sheet paints one way, and what differs between
 * listings is what the rail holds.
 *
 * Server-first: every part is a plain component. The client boundary is the
 * dialog primitive's own, which is what makes a whole opened drawer a client
 * tree in practice — a tenant composes these from a client file.
 */

/* ── the way in ───────────────────────────────────────────────────────── */

export type FilterDrawerTriggerProps = Omit<ComponentProps<typeof DrawerTrigger>, 'variant'>;

/**
 * The control that opens the sheet: the full-width inverted pill the drawer
 * primitive paints for a trigger that IS the visible control, with the
 * tenant's mark sized to the label beside it.
 */
export const FilterDrawerTrigger = ({ className, ...props }: FilterDrawerTriggerProps) => (
  <DrawerTrigger
    variant="button"
    data-slot="filter-drawer-trigger"
    className={clsxm('[&_svg]:size-5 [&_svg]:shrink-0', className)}
    {...props}
  />
);

export type FilterDrawerLabelProps = ComponentProps<'span'>;

/**
 * The words a control carries, beside whatever else it holds — the trigger's
 * mark, the apply control's count. A place rather than a paint, so it paints
 * nothing.
 */
export const FilterDrawerLabel = ({ className, ...props }: FilterDrawerLabelProps) => (
  <span
    data-slot="filter-drawer-label"
    className={optionalClassName(clsxm(className))}
    {...props}
  />
);

/* ── the sheet ────────────────────────────────────────────────────────── */

export type FilterDrawerPanelProps = Omit<ComponentProps<typeof DrawerContent>, 'side'>;

/**
 * The sheet itself, over the dialog primitive's bottom-anchored frame.
 *
 * `h-full` against the frame's own `max-h-[calc(100%-64px)]`: the sheet
 * reaches as high as the frame allows, so a short rail does not leave it
 * hovering at the foot of the screen.
 */
export const FilterDrawerPanel = ({ className, ...props }: FilterDrawerPanelProps) => (
  <DrawerContent
    side="bottom"
    data-slot="filter-drawer"
    className={clsxm('h-full', className)}
    {...props}
  />
);

export type FilterDrawerBodyProps = Omit<ComponentProps<typeof DrawerHeader>, 'variant'>;

/**
 * The column the title row and the rail stack in, and the box that scrolls
 * when the rail runs past the sheet — the primitive's scrolling header, so the
 * footer below it stays put while the facets move.
 */
export const FilterDrawerBody = ({ className, ...props }: FilterDrawerBodyProps) => (
  <DrawerHeader
    variant="scroll"
    data-slot="filter-drawer-body"
    className={optionalClassName(clsxm(className))}
    {...props}
  />
);

export type FilterDrawerBarProps = Omit<ComponentProps<typeof DrawerTitle>, 'variant'>;

/**
 * The title row: the title at one end, the dismiss at the other.
 *
 * The dialog primitive's own title element, which is what names the dialog for
 * assistive technology when the sheet opens — a heading, by the dialog's
 * contract rather than a section part choosing one. The section's no-heading
 * rule is about content a page repeats, and a dialog's name is not that.
 */
export const FilterDrawerBar = ({ className, ...props }: FilterDrawerBarProps) => (
  <DrawerTitle
    variant="heading"
    data-slot="filter-drawer-bar"
    className={optionalClassName(clsxm(className))}
    {...props}
  />
);

export type FilterDrawerTitleProps = ComponentProps<'span'>;

/** What the drawer is called. A place in the row; the type scale is the row's. */
export const FilterDrawerTitle = ({ className, ...props }: FilterDrawerTitleProps) => (
  <span
    data-slot="filter-drawer-title"
    className={optionalClassName(clsxm(className))}
    {...props}
  />
);

export type FilterDrawerCloseProps = ComponentProps<typeof DrawerClose> & {
  /**
   * Required: the mark is the control's entire visible content, so without it
   * assistive technology has nothing to call the button.
   */
  readonly 'aria-label': string;
};

/**
 * The way out at the end of the title row. What goes inside is the tenant's
 * mark, sized here; what it is called is the tenant's words.
 */
export const FilterDrawerClose = ({ className, ...props }: FilterDrawerCloseProps) => (
  <DrawerClose
    data-slot="filter-drawer-close"
    className={clsxm('[&_svg]:text-foreground [&_svg]:w-6 [&_svg]:shrink-0', className)}
    {...props}
  />
);

export type FilterDrawerRailProps = ComponentProps<'div'>;

/**
 * Where the rail stands. Its own scroll box inside the scrolling body: the
 * facets scroll under the title row rather than carrying it away with them.
 */
export const FilterDrawerRail = ({ className, ...props }: FilterDrawerRailProps) => (
  <div data-slot="filter-drawer-rail" className={clsxm('overflow-y-auto', className)} {...props} />
);

/* ── the ways out ─────────────────────────────────────────────────────── */

export type FilterDrawerFooterProps = Omit<ComponentProps<typeof DrawerFooter>, 'variant'>;

/**
 * The foot of the sheet, pinned below the body on its own raised surface — the
 * primitive's elevated footer, so the two controls stay reachable however far
 * the rail scrolls.
 */
export const FilterDrawerFooter = ({ className, ...props }: FilterDrawerFooterProps) => (
  <DrawerFooter
    variant="elevated"
    data-slot="filter-drawer-footer"
    className={optionalClassName(clsxm(className))}
    {...props}
  />
);

export type FilterDrawerApplyProps = Omit<ButtonProps, 'variant'>;

/**
 * The way onward: closes the sheet with the filters as they stand.
 *
 * `relative` is load-bearing rather than decorative — it is the frame the count
 * is positioned against, and a control composed without it would hang its count
 * off whatever ancestor happened to be positioned. So the control carries it
 * whether or not a count is composed into it.
 */
export const FilterDrawerApply = ({ className, ...props }: FilterDrawerApplyProps) => (
  <Button
    variant="primary"
    data-slot="filter-drawer-apply"
    className={clsxm('relative text-[15px] tracking-widest uppercase', className)}
    {...props}
  />
);

/**
 * `min-w-8` with `h-8`: a one-digit count sits in a square, and a four-digit
 * one widens the box rather than spilling out of it.
 */
const filterDrawerCountClasses =
  'absolute right-2 flex h-8 min-w-8 items-center justify-center rounded-md bg-background-inverted text-foreground-inverted';

export type FilterDrawerCountProps = ComponentProps<'span'>;

/**
 * How many products the filters leave, boxed at the trailing edge of the apply
 * control on the ground the control inverts. What stands in it is the tenant's:
 * a number once the listing has answered, a spinner until it has.
 */
export const FilterDrawerCount = ({ className, ...props }: FilterDrawerCountProps) => (
  <span
    data-slot="filter-drawer-count"
    className={clsxm(filterDrawerCountClasses, className)}
    {...props}
  />
);

export type FilterDrawerClearAllProps = Omit<ButtonProps, 'variant'>;

/** The way back: clears every filter and closes the sheet. */
export const FilterDrawerClearAll = ({ className, ...props }: FilterDrawerClearAllProps) => (
  <Button
    variant="ghost"
    data-slot="filter-drawer-clear-all"
    className={clsxm('text-[15px] tracking-widest uppercase', className)}
    {...props}
  />
);
